import {
  Webhook,
  ScanEye,
  Filter,
  Bell,
  Brain,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

// --- Group visual metadata (keyed by manifest group key) ---
// Groups only determine the icon and label. Color is always driven by outcome.

export const GROUP_META: Record<string, {
  icon: LucideIcon;
  label: string;
  shortLabel: string;
}> = {
  entry:            { icon: Webhook,    label: 'Entry',              shortLabel: 'En' },
  device_detection: { icon: ScanEye,    label: 'Device & Detection', shortLabel: 'DD' },
  filtering:        { icon: Filter,     label: 'Filtering',          shortLabel: 'Fi' },
  event_creation:   { icon: Bell,       label: 'Event Creation',     shortLabel: 'Ev' },
  post_alert:       { icon: Brain,      label: 'Post-Alert',         shortLabel: 'PA' },
  unknown:          { icon: HelpCircle, label: 'Other',              shortLabel: '??' },
};

/** Get group metadata, falling back to unknown. */
export function getGroupMeta(groupKey: string) {
  return GROUP_META[groupKey] ?? GROUP_META.unknown;
}

// --- Outcome styling ---

/** Maps outcome names to color classes. */
export const OUTCOME_COLORS: Record<string, { bg: string; text: string; ring: string; border: string }> = {
  Processed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', ring: 'ring-emerald-500/30', border: 'border-l-emerald-500/40' },
  Filtered:  { bg: 'bg-amber-500/15',   text: 'text-amber-400',   ring: 'ring-amber-500/30',  border: 'border-l-amber-500/40' },
  Failed:    { bg: 'bg-red-500/15',      text: 'text-red-400',     ring: 'ring-red-500/30',    border: 'border-l-red-500/40' },
  Skipped:   { bg: 'bg-gray-500/15',     text: 'text-gray-400',    ring: 'ring-gray-500/30',   border: 'border-l-gray-500/40' },
};

/** Hex colors for chart rendering. */
export const OUTCOME_HEX: Record<string, string> = {
  Processed: '#10b981',
  Filtered: '#f59e0b',
  Failed: '#ef4444',
  Skipped: '#6b7280',
};

export const OUTCOME_ORDER = ['Processed', 'Filtered', 'Failed', 'Skipped'];

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || isNaN(ms)) return '—';
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainSec = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainSec}s`;
  const hours = Math.floor(minutes / 60);
  const remainMin = minutes % 60;
  return `${hours}h ${remainMin}m`;
}

export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds == null || isNaN(seconds)) return '—';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainSec = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainSec}s`;
  const hours = Math.floor(minutes / 60);
  const remainMin = minutes % 60;
  return `${hours}h ${remainMin}m`;
}
